import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Container, Sprite } from "pixi.js";
import { Group } from "tweedle.js";
import { TransitionComponent } from "../components/Transition/TransitionComponent";
import { HighlightFramesComponent } from "../components/highlight/HighlightFramesComponent";
import { LogoComponent } from "../components/logo/LogoComponent";
import { MeterComponent } from "../components/meter/MeterComponent";
import { ReelComponent } from "../components/reels/ReelsComponent";
import { RIPFeatureComponent } from "../components/rip/RIPFeatureComponent";
import { RIPTypeTriggerComponent } from "../components/rip/RIPTypeTriggerComponent";
import { WinMeterComponent } from "../components/winmeters/WinMeterComponent";
import { Manager } from "../core/Manager";
import { DisplayComponent } from "../core/displays/DisplayComponent";
import { DisplayComponentFactory } from "../core/displays/DisplayComponentFactory";
import { DisplayObject } from "../core/displays/DisplayObject";
import { IScene } from "../core/interfaces";
import { GameConstants } from "../gameClientEvents/GameConstants";
import { Reels } from "../reels/Reels";
import { BigWin } from "./BigWin";

export abstract class BaseScene extends Container implements IScene {
    assetBundles: string[] = ["game-screen", "sound", "intro-screen", "big-win"];
    protected game = new Container();
    protected reelFrame = new Container();
    protected backgroundImageCont = new Container();
    protected backgroundSpine: Spine | null = null;
    protected reels: Reels | null = null;
    protected bigWin: BigWin | null = null;
    protected tweenGroup = new Group();

    constructor() {
        super();
    }

    public abstract constructorWithAssets(): Promise<void>;

    protected initBaseLayering(bgKey: string, spineSkel: string, spineAtlas: string) {
        this.backgroundImageCont.addChild(Sprite.from(bgKey));
        this.addChild(this.backgroundImageCont);

        this.initBackground(bgKey, spineSkel, spineAtlas);
        this.game.addChild(this.reelFrame);
        this.addChild(this.game);
    }

    protected addComponent(Comp: any, parent: Container): any {
        DisplayComponent.register(new DisplayComponentFactory(Comp));
        const component = DisplayComponent.get(Comp);
        component.init();
        //@ts-ignore
        const instance = component.getInstance();
        parent.addChild(instance);
        return instance;
    }

    protected setupCommonUI() {
        this.addComponent(LogoComponent, this.game);
        this.addComponent(HighlightFramesComponent, this.game);
        this.addComponent(MeterComponent, this.game);
        this.addComponent(RIPTypeTriggerComponent, this.game);
        this.addComponent(WinMeterComponent, this.game);
        this.addComponent(TransitionComponent, this);
        DisplayComponent.register(new DisplayComponentFactory(RIPFeatureComponent));
        DisplayComponent.get(RIPFeatureComponent).init();
    }

    protected initBackground(bgKey: string, spineSkel: string, spineAtlas: string) {
        this.backgroundImageCont.addChild(Sprite.from(bgKey));
        this.addChild(this.backgroundImageCont);

        this.backgroundSpine = Spine.from({ skeleton: spineSkel, atlas: spineAtlas });
        this.backgroundSpine.position.set(GameConstants.BACKGROUND_SIZE / 2, GameConstants.BACKGROUND_SIZE / 2);
        this.game.addChild(this.backgroundSpine);
    }

    protected setupReelFrame(skel: string, atlas: string, animate: boolean) {
        const reelBack = Spine.from({ skeleton: skel, atlas: atlas });
        reelBack.state.setAnimation(0, "animation", true);
        reelBack.position.set(0, 200);
        this.reelFrame.addChild(reelBack);

        this.reels.position.set(-463, -270);
        this.reelFrame.addChild(this.reels);

        this.reelFrame.eventMode = "static";

        const alpha = animate ? 0 : 1;
        this.reelFrame.alpha = this.backgroundImageCont.alpha = this.backgroundSpine!.alpha = alpha;
        this.backgroundSpine!.state.setAnimation(0, "animation", true);
    }

    public update(): void {
        this.tweenGroup.update();
        this.reels?.update();
        this.bigWin?.update();
    }

    public resize(screenWidth: number, screenHeight: number): void {
        const isLandscape = screenWidth > screenHeight;
        const scale = isLandscape ? Math.min(screenWidth / GameConstants.GAME_RES_WIDTH, screenHeight / GameConstants.GAME_RES_HEIGHT) : Math.min(screenWidth / GameConstants.GAME_RES_HEIGHT, screenHeight / GameConstants.GAME_RES_WIDTH);
        const bgScale = Math.max(scale, screenWidth / GameConstants.BACKGROUND_SIZE, screenHeight / GameConstants.BACKGROUND_SIZE);

        this.game.scale.set(scale);
        this.game.position.set(-(GameConstants.BACKGROUND_SIZE * scale - screenWidth) / 2, -(GameConstants.BACKGROUND_SIZE * scale - screenHeight) / 2);

        this.backgroundImageCont.scale.set(bgScale);
        this.backgroundImageCont.position.set(-(GameConstants.BACKGROUND_SIZE * bgScale - screenWidth) / 2, -(GameConstants.BACKGROUND_SIZE * bgScale - screenHeight) / 2);

        this.reelFrame.scale.set(isLandscape ? 1 : 1);
        this.reelFrame.position.set(GameConstants.BACKGROUND_SIZE / 2, isLandscape ? 815 : 815);

        [this.game, this].forEach(cont => cont.children.forEach(c => {
            if (c instanceof DisplayObject) c.setPositionResize(Manager.Orientation);
        }));

        this.bigWin?.scale.set(scale);
        this.bigWin?.position.set(screenWidth / 2, screenHeight / 2);
    }

    override destroy() {
        [LogoComponent, MeterComponent, RIPTypeTriggerComponent, ReelComponent, HighlightFramesComponent, WinMeterComponent, TransitionComponent]
            .forEach((c: any) => DisplayComponent.unregister(c));
        this.reels?.destroy();
        this.reels = null;
        this.tweenGroup?.removeAll();
        this.tweenGroup = null;
        super.destroy();
    }
}
